import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import GlobalStyles from "@mui/material/GlobalStyles";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Settings } from "luxon";
import { SnackbarProvider } from "notistack";
import { lazy, Suspense, useEffect, useState } from "react";
import { QueryCache, QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { atom, RecoilRoot, useRecoilValue } from "recoil";
import { setLocale } from "yup";
import { makeStyles } from "tss-react/mui";
import { MaterialDesignContent } from "notistack";
import { styled } from "@mui/material/styles";

/* ------------------------------------------------------------------ *
 * Imports statiques : uniquement ce qui est présent sur TOUTES les
 * routes. Tout le reste passe en lazy plus bas.
 * ------------------------------------------------------------------ */
import { AuthProvider, Roles } from "./AuthProvider";
import { IndustryIntlProvider } from "./Industry";
import Layout from "./Layout/Layout";
import LayoutWithoutNav from "./Layout/LayoutWithoutNav";
import { initCurrentLocale, Locale, YUP_LOCALES } from "./Locale";
import { initMui } from "./MuiLicence";
import { SCROLL_BAR_STYLES, themeOptions } from "./Theme";
import ProtectedRoute from "./ProtectedRoute";
import ThemeColorSync from "./ThemeColorSync";
import PageLoader from "./Layout/PageLoader";
import ChunkErrorBoundary from "./ErrorBoundary/ChunkErrorBoundary";

/* Atom extrait de useDataAssistant : importer le hook ici ramènerait tout
   le module DataAssistant dans main.js malgré le lazy sur la route. */
import { conversationResetAtom } from "./DataAssistant/atoms";
import MLAnalysis from "./MachineLearning/MLAnalysis/MLAnalysis";
import FreshFilters from "./Alert/Filter/FreshFilters";
import MLForecastAnalysis from "./MachineLearning/MLForecastAnalysis/MLForecastAnalysis";
// Evaluated at startup: it captures the installed display mode before any
// fullscreen, which the lazy pages that use it may only reach afterwards.
import "./services/displayMode";

/* ------------------------------------------------------------------ *
 * Routes en chargement différé.
 * Chaque import() crée un chunk séparé, téléchargé à la navigation.
 * ------------------------------------------------------------------ */
const Dashboard = lazy(() => import("./Dashboard/Dashboard"));
const Reporting = lazy(() => import("./Reporting/Reporting"));
const Event = lazy(() => import("./Event/Event"));
const Pricing = lazy(() => import("./Pricing/Pricing"));
const CalendarPage = lazy(() => import("./Calendar/CalendarPage"));
const ConfigurationView = lazy(
  () => import("./Configuration/ConfigurationView")
);
const LearningModels = lazy(() => import("./LearningModels/LearningModels"));
const FileManager = lazy(() => import("./FileTransfert/FileManager"));
const Users = lazy(() => import("./Users/Users"));
const Modules = lazy(() => import("./Modules/Modules"));
const Customers = lazy(() => import("./Customers/Customers"));
const Administration = lazy(() => import("./Administration/Administration"));
const DataHealthPage = lazy(
  () => import("./Administration/DataHealth/DataHealthPage")
);
const GroupQuotation = lazy(() => import("./GroupQuotation/GroupQuotation"));
const Alert = lazy(() => import("./Alert/Alert"));
const Cockpit = lazy(() => import("./Cockpit/Cockpit"));
const BannerPage = lazy(() => import("./Banner/BannerPage"));
const AppSettings = lazy(() => import("./AppSettings/AppSettings"));
const Logout = lazy(() => import("./Logout/Logout"));
const LegacyCockpitRedirect = lazy(
  () => import("./Cockpit/LegacyCockpitRedirect")
);

const DataAssistantLayout = lazy(
  () => import("./DataAssistant/DataAssistantLayout")
);
const DataAssistant = lazy(() => import("./DataAssistant/DataAssistant"));
const SearchPage = lazy(() => import("./DataAssistant/SearchPage"));
const ProjectsPage = lazy(() => import("./DataAssistant/ProjectsPage"));

const ConnectivityConfig = lazy(
  () => import("./Connectivity/ConnectivityConfig")
);

/* Exports nommés : lazy() exige un export default, on le fabrique. */
const AutoOnbPage = lazy(() =>
  import("./Connectivity/ServiceV2/AutoOnbPage").then((m) => ({
    default: m.AutoOnbPage,
  }))
);
const AdvancedSettingsPage = lazy(() =>
  import("./Connectivity/AdvancedSettingsPage").then((m) => ({
    default: m.AdvancedSettingsPage,
  }))
);
const ActivityLogPage = lazy(() =>
  import("./Connectivity/ActivityLog/ActivityLogPage").then((m) => ({
    default: m.ActivityLogPage,
  }))
);
const ServicesPage = lazy(() =>
  import("./Connectivity/ServicesPage").then((m) => ({
    default: m.ServicesPage,
  }))
);

const EventMasterPage = lazy(() =>
  import("./EventMaster/EventMasterPage").then((m) => ({
    default: m.EventMasterPage,
  }))
);

const SweepPage = lazy(() =>
  import("./Sweep/SweepPage").then((m) => ({
    default: m.SweepPage,
  }))
);

export const localeState = atom<Locale>({
  key: "localeState",
  default: initCurrentLocale(),
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error: any) => {
      if (error.message === "Unauthorized") {
        return window.location.replace(`${window.origin}/login`);
      }
    },
  }),
});

initMui();

const SnackbarStyles = styled(MaterialDesignContent)(({ theme }) => ({
  "&.notistack-MuiContent-success": {
    backgroundColor: theme.vars.palette.success.main,
    color: theme.vars.palette.success.contrastText,
    "& .MuiButton-root:hover": {
      backgroundColor: theme.vars.palette.common.white,
      border: "none",
      color: theme.vars.palette.success.main,
    },
  },
  "&.notistack-MuiContent-error": {
    backgroundColor: theme.vars.palette.error.main,
    color: theme.vars.palette.error.contrastText,
    "& .MuiButton-root:hover": {
      backgroundColor: theme.vars.palette.common.white,
      border: "none",
      color: theme.vars.palette.error.main,
    },
  },
  "&.notistack-MuiContent-warning": {
    backgroundColor: theme.vars.palette.warning.main,
    color: theme.vars.palette.warning.contrastText,
    "& .MuiButton-root:hover": {
      backgroundColor: theme.vars.palette.common.white,
      border: "none",
      color: theme.vars.palette.warning.main,
    },
  },
  "&.notistack-MuiContent-info": {
    backgroundColor: theme.vars.palette.info.main,
    color: theme.vars.palette.info.contrastText,
    "& .MuiButton-root:hover": {
      backgroundColor: theme.vars.palette.common.white,
      border: "none",
      color: theme.vars.palette.info.main,
    },
  },
}));

const useStyles = makeStyles()({
  root: {
    left: "84px",
  },
});

function AppContent() {
  const { classes } = useStyles();
  const locale = useRecoilValue(localeState);
  const conversationResetCount = useRecoilValue(conversationResetAtom);
  const [initTrad, setInitTrad] = useState(false);

  useEffect(() => {
    setLocale(YUP_LOCALES[locale]);
    Settings.defaultLocale = locale;
    setInitTrad(true);
  }, [locale]);

  return (
    <IndustryIntlProvider locale={locale}>
      <SnackbarProvider
        maxSnack={4}
        autoHideDuration={3000}
        Components={{
          error: SnackbarStyles,
          success: SnackbarStyles,
          warning: SnackbarStyles,
          info: SnackbarStyles,
        }}
        classes={{
          containerRoot: classes.root,
        }}
      >
        <ThemeColorSync />
        <Router>
          <GlobalStyles styles={SCROLL_BAR_STYLES} />
          <LocalizationProvider
            adapterLocale={locale}
            dateAdapter={AdapterLuxon}
          >
            {initTrad && (
              <QueryClientProvider client={queryClient}>
                <ChunkErrorBoundary>
                  {/* Backstop only: each layout has its own Suspense around its
                      <Outlet />, so a route chunk suspends below the menu. This
                      one catches the lazy layouts themselves. */}
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route element={<LayoutWithoutNav />}>
                        <Route path="/logout/" element={<Logout />} />
                      </Route>
                      <Route element={<AuthProvider />}>
                        <Route element={<Layout />}>
                          {["/", "/dashboard/"].map((path, index) => (
                            <Route
                              key={"dashboard-" + index}
                              element={
                                <ProtectedRoute routeName={"Dashboard"} />
                              }
                            >
                              <Route path={path} element={<Dashboard />} />
                            </Route>
                          ))}
                          <Route
                            element={
                              <ProtectedRoute routeName={"Vue hélicoptère"} />
                            }
                          >
                            <Route
                              path="/reporting/*"
                              element={<Reporting />}
                            />
                          </Route>
                          <Route
                            element={<ProtectedRoute routeName={"Events"} />}
                          >
                            <Route
                              path="/events/"
                              element={
                                <FreshFilters key="events">
                                  <Event />
                                </FreshFilters>
                              }
                            />
                          </Route>
                          <Route
                            element={<ProtectedRoute routeName={"Pricing"} />}
                          >
                            <Route
                              path="/price/"
                              element={
                                <FreshFilters key="pricing">
                                  <Pricing />
                                </FreshFilters>
                              }
                            />
                          </Route>
                          <Route
                            element={
                              <ProtectedRoute
                                routeName={"Mass Pricing (beta)"}
                              />
                            }
                          >
                            <Route
                              path="/calendar/"
                              element={<CalendarPage />}
                            />
                          </Route>
                          <Route
                            element={
                              <ProtectedRoute
                                routeName={"Configuration (new)"}
                              />
                            }
                          >
                            <Route
                              path="/configuration/*"
                              element={<ConfigurationView />}
                            />
                          </Route>
                          <Route
                            element={
                              <ProtectedRoute routeName={"Learning Models"} />
                            }
                          >
                            <Route
                              path="/learning-models/"
                              element={<LearningModels />}
                            />
                          </Route>
                          <Route
                            element={
                              <ProtectedRoute routeName={"File Manager"} />
                            }
                          >
                            <Route
                              path="/file-manager/"
                              element={<FileManager />}
                            />
                          </Route>

                          <Route
                            element={<ProtectedRoute routeName={"Users"} />}
                          >
                            <Route path="/users/" element={<Users />} />
                          </Route>

                          <Route
                            element={<ProtectedRoute routeName={"Modules"} />}
                          >
                            <Route path="/modules/" element={<Modules />} />
                          </Route>
                          <Route
                            element={<ProtectedRoute routeName={"Customers"} />}
                          >
                            <Route path="/customers/" element={<Customers />} />
                          </Route>
                          <Route
                            path="/ml-analysis/"
                            element={<MLAnalysis />}
                          />
                          <Route
                            path="/ml-forecast-analysis/"
                            element={<MLForecastAnalysis />}
                          />
                          <Route
                            element={
                              <ProtectedRoute routeName={"Administration"} />
                            }
                          >
                            <Route
                              path="/administration/"
                              element={<Administration />}
                            />
                          </Route>
                          <Route
                            element={
                              <ProtectedRoute routeName={"Connectivity"} />
                            }
                          >
                            <Route
                              path="/connectivity/"
                              element={<ConnectivityConfig />}
                            />
                            <Route
                              path="/connectivity/auto-onboarding/"
                              element={<AutoOnbPage />}
                            />
                            <Route
                              path="/connectivity/advanced-settings/"
                              element={<AdvancedSettingsPage />}
                            />
                            <Route
                              path="/connectivity/activity-log/"
                              element={<ActivityLogPage />}
                            />
                            <Route
                              path="/connectivity/services/"
                              element={<ServicesPage />}
                            />
                            <Route
                              path="/connectivity/services/:schema/:id"
                              element={<ServicesPage />}
                            />
                          </Route>
                          <Route
                            element={
                              <ProtectedRoute routeName={"Group quotation"} />
                            }
                          >
                            <Route
                              path="/groupquotation/"
                              element={<GroupQuotation />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedRoute
                                routeName={"Alertes utilisateur"}
                              />
                            }
                          >
                            <Route
                              path={"/alerte/hebergements/"}
                              element={
                                <FreshFilters key="alerts">
                                  <Alert />
                                </FreshFilters>
                              }
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedRoute
                                routeName={"Alertes utilisateur"}
                              />
                            }
                          >
                            <Route path="/cockpit/" element={<Cockpit />} />
                          </Route>

                          <Route
                            path="/data-assistant/"
                            element={<DataAssistantLayout />}
                          >
                            <Route
                              index
                              element={
                                <DataAssistant key={conversationResetCount} />
                              }
                            />
                            <Route path="search/" element={<SearchPage />} />
                            <Route
                              path="projects/"
                              element={<ProjectsPage />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedRoute requiredRole={Roles.SuperAdmin} />
                            }
                          >
                            <Route path="/banner/" element={<BannerPage />} />
                            <Route
                              path="/event-master/"
                              element={<EventMasterPage />}
                            />
                            <Route path="/sweep/" element={<SweepPage />} />
                            <Route
                              path="/administration/data-health/"
                              element={<DataHealthPage />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedRoute routeName={"File management"} />
                            }
                          >
                            <Route
                              path={"/filemanager/"}
                              element={<FileManager />}
                            />
                          </Route>

                          <Route
                            element={<ProtectedRoute routeName={"Settings"} />}
                          >
                            <Route
                              path="/settings/"
                              element={<AppSettings />}
                            />
                          </Route>

                          <Route
                            path="/revbell/alerte/hebergements/cockpit/"
                            element={<LegacyCockpitRedirect />}
                          />
                          <Route
                            path={"/home"}
                            Component={() => {
                              window.location.replace(
                                `${window.location.origin.toString()}`
                              );
                              return null;
                            }}
                          />
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                        </Route>
                      </Route>
                    </Routes>
                  </Suspense>
                </ChunkErrorBoundary>
              </QueryClientProvider>
            )}
          </LocalizationProvider>
        </Router>
      </SnackbarProvider>
    </IndustryIntlProvider>
  );
}

function App() {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider
        theme={themeOptions}
        defaultMode="dark"
        disableTransitionOnChange
      >
        <RecoilRoot>
          <AppContent />
        </RecoilRoot>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
